import React, { useState } from 'react';
import styled from '@emotion/styled';
import TouchedWrapper from '~Scenes/SelfServiceKiosks/Components/TouchedWrapper';
import PlusCircleIcon from '~Scenes/SelfServiceKiosks/Assets/Icons/plus-circle.svg';
import { ImageSize } from '../GeneralItem';
import Icon from '~ComponentsImproved/Icon';
import useI18n from '~Translations';

const Wrapper = styled(TouchedWrapper)`
  display: flex;
  border-bottom: solid 1px rgb(224, 224, 227);
  margin: 0 0 4px 0;
  padding: 0 0 4px 0;
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 17px 0 0 0;
`;

const Image = styled.div<{ url: any; imageSize: ImageSize; isUnavailable: boolean }>`
  width: ${({ imageSize }) => imageSize.width}px;
  height: ${({ imageSize }) => imageSize.height}px;
  background-image: url(${(props) => (props.url ? props.url : 'none')});
  background-size: cover;
  background-position: center;
  opacity: ${({ isUnavailable }) => (isUnavailable ? 0.6 : 1)};
`;

const Title = styled.h3<{ isUnavailable: boolean }>`
  color: ${({ isUnavailable }) => (isUnavailable ? '#9e9ea1' : 'rgb(33, 33, 36)')};
  font-family: Eina01-SemiBold;
  font-size: 24px;
  font-weight: 600;
  line-height: 28px;
  margin: 0 0 2px 0;
`;

const Price = styled.p<{ isUnavailable: boolean }>`
  color: ${({ isUnavailable }) => (isUnavailable ? '#757578' : 'rgb(33, 33, 36)')};
  font-family: Eina01-Regular;
  font-size: 24px;
  font-weight: normal;
  line-height: 32px;
  margin: 0;
`;

const Add = styled.div<{ isUnavailable: boolean }>`
  display: flex;
  align-items: center;
  padding: 0 32px 0 0;
  color: ${({ isUnavailable }) => (isUnavailable ? '#bdbdc0' : 'rgb(117, 117, 120)')};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  line-height: 24px;
  text-align: right;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AddToCartOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 20;
  border-radius: 40px;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;

  &.active {
    opacity: 1;
  }
`;

const AddedText = styled.div`
  color: #ffffff;
  font-size: 28px;
  font-weight: 600;
  text-align: center;
  padding: 16px 24px;
  background-color: rgba(0, 0, 0, 0.85);
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const InstantAddIndicator = styled.div`
  position: absolute;
  top: 16px;
  right: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 50%;
  width: 56px;
  height: 56px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
`;

interface Props {
  onClick: () => void;
  isInstant: Boolean;
  title: string;
  price: string;
  imageUrl: string;
  addText: string;
  isUnavailable: boolean;
  imageSize: ImageSize;
}

const Item = ({
  onClick,
  isInstant,
  title,
  price,
  imageUrl,
  addText,
  isUnavailable,
  imageSize,
}: Props) => {
  const { i18n } = useI18n();
  const [animate, setAnimate] = useState(false);

  const instantClick = () => {
    if (isInstant) {
      setAnimate(true);
      setTimeout(() => {
        setAnimate(false);
      }, 600);
    }

    onClick();
  };

  return (
    <Wrapper onClick={instantClick}>
      <AddToCartOverlay className={animate ? 'active' : ''}>
        <AddedText>{i18n('Express.Item.AddToCart')}</AddedText>
      </AddToCartOverlay>
      <Info>
        <Title isUnavailable={isUnavailable}>{title}</Title>
        <PriceContainer>
          <Price isUnavailable={isUnavailable}>{price}</Price>
          {isInstant && !isUnavailable && (
            <InstantAddIndicator>
              <Icon src={PlusCircleIcon} color="var(--colorWhite)" width="24px" height="24px" />
            </InstantAddIndicator>
          )}
        </PriceContainer>
      </Info>
      <Add isUnavailable={isUnavailable}>
        <span>{addText}</span>
      </Add>
      <Image url={imageUrl} imageSize={imageSize} isUnavailable={isUnavailable} />
    </Wrapper>
  );
};

export default Item;
